import React from 'react';
import moment from 'moment-timezone';
import CurrentUser from 'auth/CurrentUser';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachAvatarWithSubject from 'coaching/components/CoachAvatarWithSubject';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';

import { CoachingSessionSchedule } from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { userReadableDayAndTimeFormatLocalTZ } from 'lib/dateTimeFormat';

export function CoachSidebarHeader({
  coachName,
  subjectName,
  hideBottomDivider = false,
}: {
  coachName?: string;
  subjectName: string;
  hideBottomDivider?: boolean;
}) {
  return (
    <>
      <BoxBody size="xsmall">
        <CoachAvatarWithSubject subjectName={subjectName} coachFullName={coachName} />{' '}
      </BoxBody>
      {!hideBottomDivider && <Divider size="flush" />}
    </>
  );
}

function getRecurrenceStringForRecurringSchedule(frequency: string) {
  switch (frequency) {
    case 'Weekly':
      return 'Every';
    case 'Every other week':
      return 'Every other';
    default:
      return 'Unknown frequency,';
  }
}

// returns a Text element that renders as follows. wraps on the dot character
// if the string is long enough that the `min` label would be orphaned
// Every Wednesday at 6:00 PM PST • 40 min
export function getScheduleDescriptionForSidebar(schedule: CoachingSessionSchedule) {
  const recurrenceString = getRecurrenceStringForRecurringSchedule(schedule.description);
  const dayAndTimeString = userReadableDayAndTimeFormatLocalTZ(schedule.startDateTime);
  const description = `${recurrenceString} ${dayAndTimeString} `;
  const CHAR_WRAP_LIMIT = 30;

  return (
    <Text data-test="schedule-description" size="xsmall" weight="semibold" element="div">
      {recurrenceString} {dayAndTimeString}
      {description.length > CHAR_WRAP_LIMIT && <br />} • {schedule.duration} min
    </Text>
  );
}

function hasMultipleRecurringSessionsFeature() {
  const currentUser = CurrentUser.getUser();
  return Boolean(currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21'));
}

export function CoachingSessionScheduleRow({
  schedule,
  onClick,
}: {
  schedule: CoachingSessionSchedule;
  onClick: () => void;
}) {
  const startDateMoment = moment(schedule.startDateTime);

  return hasMultipleRecurringSessionsFeature() ? (
    <Row size="xxxxsmall">
      <BoxBody size="xsmall">
        {getScheduleDescriptionForSidebar(schedule)}
        <Button
          data-test="new-cancel-sessions-button"
          className="ManageRecurringSessionSidebar-cancelSessionsButton"
          onClick={onClick}
          level="icon"
          color="mascot">
          <MediaObject gutter="xsmall">
            <Text weight="semibold">Cancel sessions</Text>
            <Text weight="semibold" size="xxxsmall">
              <Icon size="inherit" name="chevron-right" />
            </Text>
          </MediaObject>
        </Button>
      </BoxBody>
    </Row>
  ) : (
    <span onClick={onClick}>
      <Row size="xsmall">
        <BoxBody size="xsmall">
          <MediaObject figure="right">
            <Text size="xsmall">
              {schedule.description} {'\u2022'} {startDateMoment.format('dddd')}s at{' '}
              {startDateMoment.format('LT')}
            </Text>
            <Button level="icon">
              <Icon name="chevron-right"></Icon>
            </Button>
          </MediaObject>
        </BoxBody>
        <Divider size="flush" />
      </Row>
    </span>
  );
}
